import classNames from 'classnames'
import { SourceCard } from 'components/source-card'
import React, { useMemo } from 'react'
import * as GlobalTypes from '../types'
import './styles.scss'

interface SourceGridProps {
  sources: GlobalTypes.Source[]
  selectedSources: GlobalTypes.Source[]
  onSelectSource: (source: GlobalTypes.Source) => void
  onPreviewSource?: (source: GlobalTypes.Source) => void
  searchTerm?: string
  loading?: boolean
  compact?: boolean
  showMetrics?: boolean
  maxSourcesReached?: boolean
  className?: string
}

export const SourceGrid: React.FC<SourceGridProps> = ({
  sources,
  selectedSources,
  onSelectSource,
  onPreviewSource,
  searchTerm = '',
  loading = false,
  compact = false,
  showMetrics = false,
  maxSourcesReached = false,
  className,
}) => {
  // Create a set of selected source IDs for efficient lookup
  const selectedSourceIds = useMemo(() => new Set(selectedSources.map(source => source.id)), [selectedSources])

  // Check if a source is selected
  const isSourceSelected = (source: GlobalTypes.Source): boolean => {
    return selectedSourceIds.has(source.id)
  }

  // Check if source selection should be disabled
  const isSourceDisabled = (source: GlobalTypes.Source): boolean => {
    return maxSourcesReached && !isSourceSelected(source)
  }

  if (loading) {
    return (
      <div className={classNames('source-grid', className)}>
        <div className="source-grid__loading">
          <div className="source-grid__loading-text">Loading sources...</div>
        </div>
      </div>
    )
  }

  if (sources.length === 0) {
    return (
      <div className={classNames('source-grid', className)}>
        <div className="source-grid__empty">
          <div className="source-grid__empty-icon">🔍</div>
          <div className="source-grid__empty-title">No sources found</div>
          <div className="source-grid__empty-description">
            {searchTerm
              ? `No sources match "${searchTerm}". Try adjusting your search or filters.`
              : 'No sources available. Try adjusting your filters.'}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className={classNames('source-grid', {
        'source-grid--compact': compact,
        [className || '']: !!className,
      })}
    >
      <div className="source-grid__container">
        {sources.map(source => (
          <SourceCard
            key={source.id}
            source={source}
            isSelected={isSourceSelected(source)}
            onSelect={onSelectSource}
            onPreview={onPreviewSource}
            searchTerm={searchTerm}
            compact={compact}
            showMetrics={showMetrics}
            disabled={isSourceDisabled(source)}
          />
        ))}
      </div>

      {maxSourcesReached && (
        <div className="source-grid__limit-notice">
          <div className="source-grid__limit-notice-content">
            <span className="source-grid__limit-notice-icon">⚠️</span>
            <span className="source-grid__limit-notice-text">
              Source limit reached. Deselect sources to add new ones.
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
